import { Link } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ArticleCard } from '../components/ArticleParts';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { knowledgeArticles } from '../content/knowledge';

const FAQ = [
  {
    q: 'Was ist Felix Genuss?',
    a: 'Felix Genuss ist eine Plattform rund um Rezepte, Zutatenwissen und Genuss für Hunde – mit besonderem Blick auf kleine vierbeinige Feinschmecker.',
  },
  {
    q: 'Sind die Rezepte für jeden Hund geeignet?',
    a: 'Nicht automatisch. Alter, Gewicht, Aktivität, Erkrankungen, Allergien und individuelle Bedürfnisse können Einfluss auf die Ernährung haben. Unsere Rezeptangaben sind eine Orientierung und ersetzen keine individuelle Beratung.',
  },
  {
    q: 'Ersetzt Felix Genuss eine tierärztliche Beratung?',
    a: 'Nein. Die Inhalte dienen der allgemeinen Information und Inspiration und ersetzen keine individuelle tierärztliche oder tierernährungsmedizinische Beratung.',
  },
  {
    q: 'Sind alle Rezepte glutenfrei?',
    a: 'Glutenfreie Rezepte bilden einen wichtigen Bereich von Felix Genuss. Bei jedem Rezept ist klar angegeben, welche Eigenschaften tatsächlich zutreffen – transparent und ohne pauschale Versprechen.',
  },
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

export default function Wissen() {
  return (
    <>
      <Seo
        title="Hundeernährung & Zutatenwissen | Felix Genuss"
        description="Verständliches Wissen rund um Hundeernährung: geeignete und gefährliche Lebensmittel, Portionsgrössen, Futterumstellung und glutenfreie Ernährung."
        path="/wissen"
        jsonLd={faqJsonLd}
      />
      <Breadcrumbs items={[{ label: 'Startseite', href: '/' }, { label: 'Wissen' }]} />

      <div className="container-site max-w-3xl pt-10">
        <p className="eyebrow">Zutatenwissen</p>
        <h1 className="mt-3 font-serif text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">
          Wissen rund um Hundeernährung
        </h1>
        <p className="mt-6 leading-relaxed text-brand-brown/85">
          Was darf mein Hund essen? Welche Zutaten eignen sich für selbst zubereitete Gerichte? Und worauf sollte ich bei besonderen Zutaten achten? In diesem Bereich sammeln wir verständliche Beiträge rund um Lebensmittel und Hundeernährung – ehrlich recherchiert und ohne leere Versprechen.
        </p>
        <p className="mt-4 text-sm leading-relaxed text-brand-brown-soft">
          Unser Wissen dient der allgemeinen Information und ersetzt keine individuelle tierärztliche oder tierernährungsmedizinische Beratung.
        </p>
      </div>

      <section className="container-site mt-14" aria-label="Wissensartikel">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {knowledgeArticles.map((a) => (
            <ArticleCard key={a.slug} article={a} basePath="/wissen" />
          ))}
        </div>
      </section>

      <section className="container-site mt-16 max-w-3xl" aria-labelledby="wissen-rezepte-hinweis">
        <div className="border-l-2 border-brand-gold bg-brand-beige p-6">
          <h2 id="wissen-rezepte-hinweis" className="font-serif text-xl font-bold">Vom Wissen in den Napf</h2>
          <p className="mt-3 text-sm leading-relaxed text-brand-brown">
            Das Gelernte direkt anwenden? In unserer{' '}
            <Link to="/rezepte" className="link-inline">Rezeptküche</Link> findest du Gerichte, die dieses Wissen in die Praxis umsetzen – etwa die{' '}
            <Link to="/rezepte/haehnchen-quinoa-bowl" className="link-inline">Hähnchen-Quinoa-Bowl</Link>.
          </p>
        </div>
      </section>

      <section className="container-site mt-16 max-w-3xl" aria-labelledby="faq-heading">
        <p className="eyebrow">Häufige Fragen</p>
        <h2 id="faq-heading" className="mt-3 font-serif text-2xl font-bold sm:text-3xl">Gut zu wissen</h2>
        <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
          {FAQ.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="border-brand-line">
              <AccordionTrigger className="text-left font-serif text-xl font-semibold text-brand-brown hover:text-brand-olive" data-testid={`faq-question-${i}`}>
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-base leading-relaxed text-brand-brown/85" data-testid={`faq-answer-${i}`}>
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </>
  );
}
